import { supabase, tiendaDb } from '../lib/supabase';

// ─── CLIENTE CENTRALIZADO DEL ESQUEMA TIENDA ─────────────────────────────────
// Todas las consultas al esquema "tienda" deben usar tiendaDb.from("tabla")
// Nunca usar supabase.from("tienda.tabla") — produce "Invalid schema: tienda"

// ─── PRODUCTOS ───────────────────────────────────────────────────────────────
export const productosService = {
  getAll: async ({ search = '', categoriaId = null, marcaId = null, proveedorId = null, activo = null } = {}) => {
    let query = tiendaDb?.from('productos')?.select(`
        *,
        categoria:categorias(id, nombre, familia_id, margen_objetivo),
        marca:marcas(id, nombre),
        proveedor:proveedores(id, nombre)
      `)?.order('nombre');

    if (search) {
      query = query?.or(
        `nombre.ilike.%${search}%,codigo_interno.ilike.%${search}%,ean.ilike.%${search}%,plu.ilike.%${search}%,referencia_proveedor.ilike.%${search}%`
      );
    }
    if (categoriaId) query = query?.eq('categoria_id', categoriaId);
    if (marcaId) query = query?.eq('marca_id', marcaId);
    if (proveedorId) query = query?.eq('proveedor_principal_id', proveedorId);
    if (activo !== null) query = query?.eq('activo', activo);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getById: async (id) => {
    const { data, error } = await tiendaDb?.from('productos')?.select(`
        *,
        categoria:categorias(id, nombre),
        marca:marcas(id, nombre),
        proveedor:proveedores(id, nombre)
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return data;
  },

  create: async (producto) => {
    // Map precio_compra → precio_compra (column added by migration)
    // Map iva_compra → tipo_iva (existing column) if iva_compra not yet added
    const {
      stock_disponible, precio_venta, precio_coste,
      categoria, marca, proveedor,
      ...safeProducto
    } = producto;
    const { data, error } = await tiendaDb?.from('productos')?.insert(safeProducto)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const {
      stock_disponible, id: _id,
      categoria, marca, proveedor,
      precio_venta, precio_coste,
      ...safeUpdates
    } = updates;
    const { data, error } = await tiendaDb?.from('productos')
      ?.update({ ...safeUpdates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  toggleActivo: async (id, activo) => {
    const { data, error } = await tiendaDb?.from('productos')
      ?.update({ activo, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ─── INVENTARIO ──────────────────────────────────────────────────────────────
export const inventarioService = {
  getAll: async ({ search = '', statusFilter = 'Todos', categoriaId = null, almacenId = null } = {}) => {
    let query = tiendaDb?.from('inventario_actual')?.select(`
        id,
        producto_id,
        almacen_id,
        stock_actual,
        stock_reservado,
        stock_disponible,
        coste_medio,
        ultimo_coste,
        ultima_actualizacion,
        producto:productos(
          id, codigo_interno, ean, plu, nombre, activo,
          stock_minimo, stock_maximo, punto_pedido,
          tipo_iva, precio_compra, pvp_venta, pvp_sin_iva,
          margen_objetivo, margen_real, bloqueo_precio,
          categoria:categorias(id, nombre, familia_id, margen_objetivo)
        ),
        almacen:almacenes(id, nombre)
      `)?.order('ultima_actualizacion', { ascending: false });

    if (almacenId) query = query?.eq('almacen_id', almacenId);

    const { data, error } = await query;
    if (error) throw error;

    let result = data || [];

    if (search) {
      const s = search?.toLowerCase();
      result = result?.filter(item =>
        item?.producto?.nombre?.toLowerCase()?.includes(s) ||
        item?.producto?.codigo_interno?.toLowerCase()?.includes(s) ||
        item?.producto?.ean?.toLowerCase()?.includes(s) ||
        item?.producto?.plu?.toLowerCase()?.includes(s)
      );
    }

    if (categoriaId) {
      result = result?.filter(i => i?.producto?.categoria?.id === categoriaId);
    }

    if (statusFilter === 'Agotado') result = result?.filter(i => (i?.stock_actual ?? 0) <= 0);
    else if (statusFilter === 'Bajo mínimo') result = result?.filter(i => (i?.stock_actual ?? 0) > 0 && (i?.stock_actual ?? 0) <= (i?.producto?.stock_minimo ?? 0));
    else if (statusFilter === 'Activo') result = result?.filter(i => (i?.stock_actual ?? 0) > (i?.producto?.stock_minimo ?? 0));

    return result;
  },

  getKPIs: async () => {
    const { data, error } = await tiendaDb?.from('inventario_actual')?.select(`
        stock_actual, stock_disponible, coste_medio,
        producto:productos(activo, stock_minimo)
      `);
    if (error) throw error;

    const items = data || [];
    const totalStock = items?.reduce((s, i) => s + (i?.stock_actual || 0), 0);
    const valorCoste = items?.reduce((s, i) => s + ((i?.stock_actual || 0) * (i?.coste_medio || 0)), 0);
    const stockMin = (i) => i?.producto?.stock_minimo ?? 0;
    const bajoMinimo = items?.filter(i => (i?.stock_actual ?? 0) > 0 && (i?.stock_actual ?? 0) <= stockMin(i))?.length;
    const agotados = items?.filter(i => (i?.stock_actual ?? 0) <= 0)?.length;

    return {
      referencias: items?.length,
      totalStock,
      valorCoste,
      bajoMinimo,
      agotados,
    };
  },
};

// ─── MOVIMIENTOS DE STOCK ─────────────────────────────────────────────────────
// La restricción de la tabla solo admite exactamente: ENTRADA, VENTA, MERMA, AJUSTE, DEVOLUCION
const TIPOS_MOVIMIENTO_VALIDOS = ['ENTRADA', 'VENTA', 'MERMA', 'AJUSTE', 'DEVOLUCION'];

export const movimientosStockService = {
  getAll: async ({ productoId = null, tipo = null, almacenId = null, dateFrom = null, dateTo = null, search = '', facturaId = null, referenciaTipo = null } = {}) => {
    let query = tiendaDb?.from('movimientos_stock')?.select(`
        id,
        producto_id,
        lote_id,
        tipo_movimiento,
        cantidad,
        coste_unitario,
        observaciones,
        usuario,
        created_at,
        almacen_origen_id,
        almacen_destino_id,
        referencia_tipo,
        referencia_id,
        producto:productos(id, nombre, codigo_interno, ean),
        almacen_origen:almacenes!movimientos_stock_almacen_origen_id_fkey(id, nombre),
        almacen_destino:almacenes!movimientos_stock_almacen_destino_id_fkey(id, nombre)
      `)?.order('created_at', { ascending: false });

    if (productoId) query = query?.eq('producto_id', productoId);
    if (tipo) query = query?.eq('tipo_movimiento', tipo?.toUpperCase());
    if (almacenId) query = query?.or(`almacen_origen_id.eq.${almacenId},almacen_destino_id.eq.${almacenId}`);
    if (dateFrom) query = query?.gte('created_at', dateFrom);
    if (dateTo) query = query?.lte('created_at', dateTo + 'T23:59:59');
    if (facturaId) query = query?.eq('referencia_id', facturaId);
    if (referenciaTipo) query = query?.eq('referencia_tipo', referenciaTipo);

    const { data, error } = await query;
    if (error) throw error;

    let result = data || [];
    if (search) {
      const s = search?.toLowerCase();
      result = result?.filter(m =>
        m?.producto?.nombre?.toLowerCase()?.includes(s) ||
        m?.producto?.codigo_interno?.toLowerCase()?.includes(s) ||
        m?.referencia_id?.toLowerCase()?.includes(s) ||
        m?.observaciones?.toLowerCase()?.includes(s)
      );
    }
    return result;
  },

  create: async (movimiento) => {
    // Insertar únicamente en tienda.movimientos_stock
    // No actualizar inventario_actual desde el frontend
    // tipo_movimiento debe ser uno de: ENTRADA, VENTA, MERMA, AJUSTE, DEVOLUCION
    const { id, producto, almacen_origen, almacen_destino, ...rest } = movimiento;
    const safeMovimiento = {
      ...rest,
      tipo_movimiento: rest?.tipo_movimiento?.toUpperCase(),
    };
    const { data, error } = await tiendaDb?.from('movimientos_stock')?.insert(safeMovimiento)?.select()?.single();
    if (error) throw error;
    return data;
  },

  getKPIs: async () => {
    const now = new Date();
    const today = now?.toISOString()?.split('T')?.[0];
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];

    const { data, error } = await tiendaDb?.from('movimientos_stock')
      ?.select('tipo_movimiento, cantidad, created_at')
      ?.gte('created_at', firstOfMonth);
    if (error) throw error;

    const all = data || [];
    const hoy = all?.filter(m => m?.created_at?.startsWith(today));
    const entradas = all?.filter(m => m?.tipo_movimiento === 'ENTRADA');
    const salidas = all?.filter(m => ['VENTA', 'MERMA']?.includes(m?.tipo_movimiento));

    return {
      totalMes: all?.length,
      hoy: hoy?.length,
      entradasMes: entradas?.reduce((s, m) => s + (m?.cantidad || 0), 0),
      salidasMes: salidas?.reduce((s, m) => s + (m?.cantidad || 0), 0),
    };
  },

  // Editar únicamente las observaciones de un movimiento existente
  updateObservaciones: async (id, observaciones) => {
    const { data, error } = await tiendaDb?.from('movimientos_stock')
      ?.update({ observaciones: observaciones || null })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Eliminar movimiento (solo administrador, sin reversión automática de inventario)
  delete: async (id) => {
    const { error } = await tiendaDb?.from('movimientos_stock')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ─── ALMACENES ────────────────────────────────────────────────────────────────
export const almacenesService = {
  getAll: async () => {
    const { data, error } = await tiendaDb?.from('almacenes')?.select('id, nombre, descripcion, activo')?.order('nombre');
    if (error) throw error;
    return data || [];
  },
};

// ─── LOTES ────────────────────────────────────────────────────────────────────
export const lotesService = {
  getAll: async ({ search = '', productoId = null } = {}) => {
    let query = tiendaDb?.from('lotes')?.select(`
        *,
        producto:productos(id, nombre, codigo_interno)
      `)?.order('fecha_entrada', { ascending: false });

    if (productoId) query = query?.eq('producto_id', productoId);

    const { data, error } = await query;
    if (error) throw error;

    let result = data || [];
    if (search) {
      const s = search?.toLowerCase();
      result = result?.filter(l =>
        l?.numero_lote?.toLowerCase()?.includes(s) ||
        l?.producto?.nombre?.toLowerCase()?.includes(s)
      );
    }
    return result;
  },

  create: async (lote) => {
    const { data, error } = await tiendaDb?.from('lotes')?.insert(lote)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('lotes')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── CATEGORÍAS ──────────────────────────────────────────────────────────────
export const categoriasService = {
  getAll: async ({ familiaId = null } = {}) => {
    let query = tiendaDb?.from('categorias')?.select('id, nombre, descripcion, activo, familia_id, margen_objetivo, regla_redondeo, orden')?.eq('activo', true)?.order('nombre');
    if (familiaId) query = query?.eq('familia_id', familiaId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  create: async (categoria) => {
    const { data, error } = await tiendaDb?.from('categorias')?.insert(categoria)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('categorias')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── FAMILIAS (extended with full CRUD) ──────────────────────────────────────
export const familiasService = {
  getAll: async ({ includeInactive = false } = {}) => {
    let query = tiendaDb?.from('familias_producto')?.select('id, nombre, descripcion, activo, orden, margen_objetivo, regla_redondeo')?.order('orden', { nullsFirst: false })?.order('nombre');
    if (!includeInactive) query = query?.eq('activo', true);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  create: async (familia) => {
    const { data, error } = await tiendaDb?.from('familias_producto')?.insert(familia)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('familias_producto')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  delete: async (id) => {
    const { error } = await tiendaDb?.from('familias_producto')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  toggleActivo: async (id, activo) => {
    const { data, error } = await tiendaDb?.from('familias_producto')?.update({ activo, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── MARCAS ───────────────────────────────────────────────────────────────────
export const marcasService = {
  getAll: async () => {
    const { data, error } = await tiendaDb?.from('marcas')?.select('id, nombre, descripcion, activo')?.eq('activo', true)?.order('nombre');
    if (error) throw error;
    return data || [];
  },

  create: async (marca) => {
    const { data, error } = await tiendaDb?.from('marcas')?.insert(marca)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('marcas')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── PROVEEDORES ─────────────────────────────────────────────────────────────
export const proveedoresService = {
  getAll: async ({ includeInactive = false } = {}) => {
    let query = tiendaDb?.from('proveedores')
      ?.select('id, codigo, nombre, nombre_comercial, nif_cif, contacto_principal, telefono, email, activo, direccion, codigo_postal, localidad, provincia, pais, forma_pago, dias_pago, observaciones')
      ?.order('nombre');
    if (!includeInactive) query = query?.eq('activo', true);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  create: async (proveedor) => {
    const { data, error } = await tiendaDb?.from('proveedores')?.insert(proveedor)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('proveedores')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── COMPRAS (FACTURAS COMPRA) ────────────────────────────────────────────────
export const comprasService = {
  getAll: async ({ search = '', estado = 'Todas' } = {}) => {
    let query = tiendaDb?.from('facturas_compra')?.select(`
        id, numero_factura, fecha_factura, fecha_vencimiento, forma_pago,
        estado, estado_pago, estado_recepcion,
        base_imponible, iva, total, importe_pagado, pendiente_pago,
        fecha_pago, observaciones_pago, referencia_pago,
        ocr_procesado, ocr_confianza,
        proveedor_id, proveedor:proveedores(id, nombre, email, telefono)
      `)?.order('fecha_factura', { ascending: false });

    if (estado !== 'Todas') query = query?.eq('estado', estado?.toUpperCase());

    const { data, error } = await query;
    if (error) throw error;

    let result = data || [];
    if (search) {
      const s = search?.toLowerCase();
      result = result?.filter(f =>
        f?.numero_factura?.toLowerCase()?.includes(s) ||
        f?.proveedor?.nombre?.toLowerCase()?.includes(s)
      );
    }
    return result;
  },

  getById: async (id) => {
    const { data, error } = await tiendaDb?.from('facturas_compra')?.select(`
        *,
        proveedor:proveedores(*),
        lineas:lineas_factura_compra(
          *,
          producto:productos(id, nombre, codigo_interno, controla_lotes, controla_caducidad)
        )
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return data;
  },

  create: async (factura) => {
    const { data, error } = await tiendaDb?.from('facturas_compra')?.insert(factura)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('facturas_compra')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  getKPIs: async () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];
    const today = now?.toISOString()?.split('T')?.[0];
    const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

    const { data, error } = await tiendaDb?.from('facturas_compra')?.select(
      'total, iva, estado, estado_pago, fecha_factura, fecha_vencimiento, importe_pagado, pendiente_pago, proveedor_id'
    );
    if (error) throw error;

    const all = data || [];
    const noAnuladas = all?.filter(f => f?.estado_pago !== 'ANULADA' && f?.estado !== 'ANULADA');
    const mes = noAnuladas?.filter(f => f?.fecha_factura >= firstOfMonth);
    const pendientes = noAnuladas?.filter(f => f?.estado_pago !== 'PAGADA' && (f?.pendiente_pago ?? f?.total ?? 0) > 0);
    const vencidas = pendientes?.filter(f => f?.fecha_vencimiento && f?.fecha_vencimiento < today);
    const vence7dias = pendientes?.filter(f => f?.fecha_vencimiento && f?.fecha_vencimiento >= today && f?.fecha_vencimiento <= in7days);
    const proveedoresActivos = new Set(noAnuladas?.map(f => f?.proveedor_id)?.filter(Boolean));

    return {
      comprasMes: mes?.reduce((s, f) => s + (f?.total || 0), 0),
      ivaSoportado: mes?.reduce((s, f) => s + (f?.iva || 0), 0),
      facturasPendientes: pendientes?.length,
      pendientePago: noAnuladas?.reduce((s, f) => s + (f?.pendiente_pago ?? (f?.estado_pago !== 'PAGADA' ? (f?.total || 0) : 0)), 0),
      facturasVencidas: vencidas?.length,
      vence7dias: vence7dias?.length,
      proveedoresActivos: proveedoresActivos?.size,
    };
  },
};

// ─── PAGOS DE FACTURAS COMPRA ─────────────────────────────────────────────────
export const pagosFacturasService = {
  getByFactura: async (facturaId) => {
    const { data, error } = await tiendaDb?.from('pagos_facturas_compra')
      ?.select('*')
      ?.eq('factura_compra_id', facturaId)
      ?.order('fecha_pago', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  registrarPago: async ({ facturaId, importe, fechaPago, formaPago, referencia, observaciones, createdBy, esTotal = false }) => {
    // 1. Obtener factura actual
    const { data: factura, error: facturaError } = await tiendaDb?.from('facturas_compra')
      ?.select('total, importe_pagado, pendiente_pago')
      ?.eq('id', facturaId)
      ?.single();
    if (facturaError) throw facturaError;

    const totalFactura = parseFloat(factura?.total) || 0;
    const importePagadoActual = parseFloat(factura?.importe_pagado) || 0;
    const importeNuevo = esTotal ? totalFactura - importePagadoActual : parseFloat(importe);

    if (isNaN(importeNuevo) || importeNuevo <= 0) throw new Error('El importe debe ser mayor que 0');

    const nuevoImportePagado = Math.min(importePagadoActual + importeNuevo, totalFactura);
    const nuevoPendiente = Math.max(totalFactura - nuevoImportePagado, 0);
    const nuevoEstado = nuevoPendiente <= 0.005 ? 'PAGADA' : 'PARCIAL';

    // 2. Insertar registro de pago en historial
    const { error: pagoError } = await tiendaDb?.from('pagos_facturas_compra')?.insert({
      factura_compra_id: facturaId,
      importe: importeNuevo,
      fecha_pago: fechaPago || new Date()?.toISOString()?.split('T')?.[0],
      forma_pago: formaPago || null,
      referencia: referencia || null,
      observaciones: observaciones || null,
      created_by: createdBy || null,
    });
    if (pagoError) throw pagoError;

    // 3. Actualizar factura
    const updates = {
      importe_pagado: nuevoImportePagado,
      pendiente_pago: nuevoPendiente,
      estado_pago: nuevoEstado,
      forma_pago: formaPago || null,
    };
    if (nuevoEstado === 'PAGADA') {
      updates.fecha_pago = fechaPago || new Date()?.toISOString()?.split('T')?.[0];
    }

    const { data: updatedFactura, error: updateError } = await tiendaDb?.from('facturas_compra')
      ?.update(updates)
      ?.eq('id', facturaId)
      ?.select()
      ?.single();
    if (updateError) throw updateError;

    return updatedFactura;
  },

  volverAPendiente: async (facturaId, confirmarEliminarPagos = false) => {
    // Check if there are existing payments
    const { data: pagos, error: pagosError } = await tiendaDb?.from('pagos_facturas_compra')
      ?.select('id, importe')
      ?.eq('factura_compra_id', facturaId);
    if (pagosError) throw pagosError;

    if ((pagos?.length || 0) > 0 && !confirmarEliminarPagos) {
      throw new Error(`TIENE_PAGOS:${pagos?.length}`);
    }

    // Delete payment history if confirmed
    if (confirmarEliminarPagos && pagos?.length > 0) {
      const { error: deleteError } = await tiendaDb?.from('pagos_facturas_compra')
        ?.delete()
        ?.eq('factura_compra_id', facturaId);
      if (deleteError) throw deleteError;
    }

    // Get invoice total to reset pendiente_pago
    const { data: factura, error: facturaError } = await tiendaDb?.from('facturas_compra')
      ?.select('total')
      ?.eq('id', facturaId)
      ?.single();
    if (facturaError) throw facturaError;

    const { data, error } = await tiendaDb?.from('facturas_compra')
      ?.update({
        estado_pago: 'PENDIENTE',
        importe_pagado: 0,
        pendiente_pago: parseFloat(factura?.total) || 0,
        fecha_pago: null,
        observaciones_pago: null,
      })
      ?.eq('id', facturaId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  anularFactura: async (facturaId, observaciones) => {
    const { data, error } = await tiendaDb?.from('facturas_compra')
      ?.update({ estado_pago: 'ANULADA', observaciones_pago: observaciones || null })
      ?.eq('id', facturaId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  actualizarVencimiento: async (facturaId, fechaVencimiento, formaPago, observaciones) => {
    const { data, error } = await tiendaDb?.from('facturas_compra')
      ?.update({
        fecha_vencimiento: fechaVencimiento || null,
        forma_pago: formaPago || null,
        observaciones_pago: observaciones || null,
      })
      ?.eq('id', facturaId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ─── LÍNEAS FACTURA COMPRA ────────────────────────────────────────────────────
export const lineasCompraService = {
  insertBulk: async (lineas) => {
    if (!lineas?.length) return [];
    const { data, error } = await tiendaDb?.from('lineas_factura_compra')?.insert(lineas)?.select();
    if (error) throw error;
    return data || [];
  },

  deleteByFactura: async (facturaId) => {
    const { error } = await tiendaDb?.from('lineas_factura_compra')?.delete()?.eq('factura_id', facturaId);
    if (error) throw error;
  },
};

// ─── OCR PRODUCT MAPPINGS (Aprendizaje proveedor-producto) ────────────────────
export const ocrMappingsService = {
  // Buscar mappings para un proveedor
  getByProveedor: async (proveedorId) => {
    if (!proveedorId) return [];
    const { data, error } = await tiendaDb?.from('ocr_product_mappings')
      ?.select('*, producto:productos(id, nombre, codigo_interno, ean)')
      ?.eq('proveedor_id', proveedorId)
      ?.order('veces_confirmado', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Guardar o actualizar un mapping aprendido
  upsert: async (mapping) => {
    const { data, error } = await tiendaDb?.from('ocr_product_mappings')
      ?.upsert(mapping, { onConflict: 'proveedor_id,descripcion_proveedor' })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Incrementar contador de confirmaciones
  incrementConfirmacion: async (id) => {
    const { data: current } = await tiendaDb?.from('ocr_product_mappings')?.select('veces_confirmado')?.eq('id', id)?.single();
    const { data, error } = await tiendaDb?.from('ocr_product_mappings')
      ?.update({
        veces_confirmado: (current?.veces_confirmado || 0) + 1,
        ultima_confirmacion: new Date()?.toISOString(),
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ─── FACTURAS ENTRANTES (con foto) ───────────────────────────────────────────
export const facturasEntrantesService = {
  ...comprasService,

  uploadFoto: async (facturaId, file) => {
    const ext = file?.name?.split('.')?.pop() || 'jpg';
    const path = `facturas/${facturaId}/foto_${Date.now()}.${ext}`;

    const BUCKET = 'facturas-compras';
    console.log('[facturasEntrantesService.uploadFoto] bucket:', BUCKET);
    console.log('[facturasEntrantesService.uploadFoto] path:', path);

    const { data: { session } } = await supabase?.auth?.getSession();
    console.log('[facturasEntrantesService.uploadFoto] usuario:', session?.user?.id || 'no autenticado');

    const { data: uploadData, error: uploadError } = await supabase?.storage?.from(BUCKET)?.upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file?.type,
      metadata: {
        factura_id: facturaId,
        tipo: 'foto_factura',
        ocr_pendiente: 'true',
        ia_pendiente: 'true',
        uploaded_at: new Date()?.toISOString(),
      },
    });

    console.log('[facturasEntrantesService.uploadFoto] respuesta Supabase Storage:', { uploadData, uploadError });

    if (uploadError) throw new Error(`Error subiendo foto (bucket: ${BUCKET}, path: ${path}): ${uploadError.message}`);

    const { data: signedData, error: signedError } = await supabase?.storage?.from(BUCKET)?.createSignedUrl(path, 3600);
    if (signedError) throw signedError;

    const { data, error } = await tiendaDb?.from('facturas_compra')?.update({
      foto_url: path,
      ocr_procesado: false,
      ocr_metadata: null,
    })?.eq('id', facturaId)?.select()?.single();

    if (error) throw error;

    return { ...data, signedUrl: signedData?.signedUrl, storagePath: path };
  },

  getFotoUrl: async (storagePath) => {
    if (!storagePath) return null;
    const BUCKET = 'facturas-compras';
    console.log('[facturasEntrantesService.getFotoUrl] bucket:', BUCKET, 'path:', storagePath);
    const { data, error } = await supabase?.storage?.from(BUCKET)?.createSignedUrl(storagePath, 3600);
    if (error) {
      console.error('[facturasEntrantesService.getFotoUrl] error:', error);
      return null;
    }
    return data?.signedUrl;
  },
};

// ─── VENTAS ──────────────────────────────────────────────────────────────────
export const ventasService = {
  getAll: async ({ search = '', canal = 'Todos', estado = 'Todos', dateFrom = null, dateTo = null } = {}) => {
    let query = tiendaDb?.from('ventas')?.select(`
        *,
        cliente:clientes(id, nombre, telefono)
      `)?.order('fecha', { ascending: false });

    if (canal !== 'Todos') query = query?.eq('canal', canal?.toUpperCase());
    if (estado !== 'Todos') query = query?.eq('estado', estado?.toUpperCase());
    if (dateFrom) query = query?.gte('fecha', dateFrom);
    if (dateTo) query = query?.lte('fecha', dateTo);

    const { data, error } = await query;
    if (error) throw error;

    let result = data || [];
    if (search) {
      const s = search?.toLowerCase();
      result = result?.filter(v =>
        v?.numero_venta?.toLowerCase()?.includes(s) ||
        v?.cliente?.nombre?.toLowerCase()?.includes(s)
      );
    }
    return result;
  },

  getKPIs: async () => {
    const now = new Date();
    const today = now?.toISOString()?.split('T')?.[0];
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];

    const { data, error } = await tiendaDb?.from('ventas')?.select('total, canal, forma_pago, estado, fecha');
    if (error) throw error;

    const all = (data || [])?.filter(v => v?.estado !== 'ANULADO');
    const hoy = all?.filter(v => v?.fecha?.startsWith(today));
    const mes = all?.filter(v => v?.fecha >= firstOfMonth);

    return {
      ventasHoy: hoy?.reduce((s, v) => s + (v?.total || 0), 0),
      ventasMes: mes?.reduce((s, v) => s + (v?.total || 0), 0),
      ticketsHoy: hoy?.length,
      ticketMedio: hoy?.length > 0 ? hoy?.reduce((s, v) => s + (v?.total || 0), 0) / hoy?.length : 0,
      ventasFisico: mes?.filter(v => v?.canal === 'TIENDA')?.reduce((s, v) => s + (v?.total || 0), 0),
      ventasOnline: mes?.filter(v => v?.canal === 'ONLINE')?.reduce((s, v) => s + (v?.total || 0), 0),
    };
  },

  create: async (venta) => {
    const { data, error } = await tiendaDb?.from('ventas')?.insert(venta)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── CAJA DIARIA ─────────────────────────────────────────────────────────────
export const cajaDiariaService = {
  getAll: async ({ dateFrom = null, dateTo = null } = {}) => {
    let query = tiendaDb?.from('caja_diaria')?.select('*')?.order('fecha', { ascending: false });
    if (dateFrom) query = query?.gte('fecha', dateFrom);
    if (dateTo) query = query?.lte('fecha', dateTo);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getByFecha: async (fecha) => {
    const { data, error } = await tiendaDb?.from('caja_diaria')?.select('*')?.eq('fecha', fecha)?.maybeSingle();
    if (error) throw error;
    return data;
  },

  upsert: async (caja) => {
    const { data, error } = await tiendaDb?.from('caja_diaria')?.upsert(caja, { onConflict: 'fecha' })?.select()?.single();
    if (error) throw error;
    return data;
  },

  getKPIs: async () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];

    const { data, error } = await tiendaDb?.from('caja_diaria')?.select('*')?.gte('fecha', firstOfMonth)?.order('fecha', { ascending: false });
    if (error) throw error;

    const all = data || [];
    const totalEfectivo = all?.reduce((s, c) => s + (c?.efectivo || 0), 0);
    const totalTarjeta = all?.reduce((s, c) => s + (c?.tarjeta || 0), 0);
    const totalBizum = all?.reduce((s, c) => s + (c?.bizum || 0), 0);
    const totalGastos = all?.reduce((s, c) => s + (c?.gastos || 0), 0);
    const totalIngresos = totalEfectivo + totalTarjeta + totalBizum;

    return {
      totalIngresos,
      totalEfectivo,
      totalTarjeta,
      totalBizum,
      totalGastos,
      beneficioNeto: totalIngresos - totalGastos,
      diasRegistrados: all?.length,
      ultimaCaja: all?.[0] || null,
    };
  },
};

// ─── DASHBOARD FINANCIERO ────────────────────────────────────────────────────
export const dashboardFinancieroService = {
  getResumen: async () => {
    const [ventasKPIs, comprasKPIs, cajaKPIs, inventarioKPIs] = await Promise.all([
      ventasService?.getKPIs(),
      comprasService?.getKPIs(),
      cajaDiariaService?.getKPIs(),
      inventarioService?.getKPIs(),
    ]);

    return {
      ventas: ventasKPIs,
      compras: comprasKPIs,
      caja: cajaKPIs,
      inventario: inventarioKPIs,
      margenBruto: ventasKPIs?.ventasMes > 0
        ? ((ventasKPIs?.ventasMes - comprasKPIs?.comprasMes) / ventasKPIs?.ventasMes) * 100
        : 0,
    };
  },

  getEvolucionVentas: async (meses = 6) => {
    const now = new Date();
    const desde = new Date(now.getFullYear(), now.getMonth() - meses + 1, 1)?.toISOString()?.split('T')?.[0];

    const { data, error } = await tiendaDb?.from('ventas')?.select('fecha, total, canal')?.gte('fecha', desde)?.neq('estado', 'ANULADO')?.order('fecha');

    if (error) throw error;

    const byMonth = {};
    (data || [])?.forEach(v => {
      const mes = v?.fecha?.substring(0, 7);
      if (!byMonth?.[mes]) byMonth[mes] = { mes, fisico: 0, online: 0, total: 0 };
      if (v?.canal === 'TIENDA') byMonth[mes].fisico += v?.total || 0;
      else if (v?.canal === 'ONLINE') byMonth[mes].online += v?.total || 0;
      byMonth[mes].total += v?.total || 0;
    });

    return Object.values(byMonth)?.sort((a, b) => a?.mes?.localeCompare(b?.mes));
  },
};

// ─── HISTÓRICO PRECIOS COMPRA ─────────────────────────────────────────────────
export const historicoPreciosService = {
  getByProducto: async (productoId) => {
    if (!productoId) return [];
    const { data, error } = await tiendaDb?.from('historico_precios_compra')
      ?.select(`
        *,
        proveedor:proveedores(id, nombre),
        factura:facturas_compra(id, numero_factura, fecha_factura)
      `)
      ?.eq('producto_id', productoId)
      ?.order('fecha', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  create: async (historial) => {
    const { data, error } = await tiendaDb?.from('historico_precios_compra')?.insert(historial)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Insert multiple price history records (bulk)
  insertBulk: async (records) => {
    if (!records?.length) return [];
    const { data, error } = await tiendaDb?.from('historico_precios_compra')?.insert(records)?.select();
    if (error) throw error;
    return data || [];
  },
};

// ─── COMPLETAR IMPORTACIÓN (para facturas ya guardadas sin inventario) ────────
export const completarImportacionService = {
  // Call the transactional RPC to complete an already-saved invoice
  confirmar: async (facturaId, usuario = null) => {
    const { data, error } = await tiendaDb?.rpc('confirmar_importacion_factura', {
      p_factura_id: facturaId,
      p_usuario: usuario || null,
    });
    if (error) throw error;
    return data;
  },

  // Get lines that still need product assignment
  getLineasPendientes: async (facturaId) => {
    const { data, error } = await tiendaDb?.from('lineas_factura_compra')
      ?.select(`
        *,
        producto:productos(id, nombre, codigo_interno, ean, precio_compra, tipo_iva)
      `)
      ?.eq('factura_id', facturaId)
      ?.is('producto_id', null);
    if (error) throw error;
    return data || [];
  },

  // Update a line with product assignment
  asignarProducto: async (lineaId, productoId) => {
    const { data, error } = await tiendaDb?.from('lineas_factura_compra')
      ?.update({ producto_id: productoId })
      ?.eq('id', lineaId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Additive inventory UPSERT via RPC
  upsertInventario: async (productoId, almacenId, cantidad, costeUnitario = null) => {
    const { data, error } = await tiendaDb?.rpc('upsert_inventario_entrada', {
      p_producto_id: productoId,
      p_almacen_id: almacenId,
      p_cantidad: cantidad,
      p_coste_unitario: costeUnitario,
    });
    if (error) throw error;
    return data;
  },
};
